import { useState, useMemo, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { BookOpen, SlidersHorizontal, AlertCircle } from "lucide-react"
import CourseGrid from "../components/course/CourseGrid"
import SearchBar from "../components/SearchBar"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { directCoursesApiResponseSchema } from "../utils/schemas/cours-schema"
import { useCategories } from "../hooks/useCategories"
import { useTranslation } from "../i18n"
import { loadTranslationsForEntities } from "../utils/translationUtils"
import "../styles/home-editorial.css"

export default function CoursesPage() {
  const { tObj, language } = useTranslation()
  const [translatedCourses, setTranslatedCourses] = useState([])
  const [searchParams, setSearchParams] = useSearchParams()
  // Initialiser searchQuery directement depuis les paramètres d'URL
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || "")
  const { categories, loading: categoriesLoading } = useCategories()
  // Initialiser selectedCategory directement depuis les paramètres d'URL
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const urlCategory = searchParams.get('category')
    return urlCategory || tObj.courses.all()
  })
  const [sortBy, setSortBy] = useState("popularity") // Par défaut: popularité

  // Construire l'URL avec les paramètres de recherche pour interroger la DB directement
  const coursesUrl = useMemo(() => {
    const params = new URLSearchParams()
    
    if (searchQuery && searchQuery.trim()) {
      params.append('search', searchQuery.trim())
    }
    
    if (selectedCategory && selectedCategory !== tObj.courses.all()) {
      // Trouver l'ID de la catégorie par son nom (peut être translatedName ou name)
      const category = categories.find(cat => {
        const displayName = cat.displayName || cat.translatedName || cat.name
        return displayName === selectedCategory
      })
      if (category?.id) {
        params.append('categoryId', category.id)
      }
    }
    
    const queryString = params.toString()
    return queryString ? `${endpoints.courses}?${queryString}` : endpoints.courses
  }, [searchQuery, selectedCategory, categories])

  // Fetch courses avec les paramètres de recherche
  const {
    data: coursesData,
    loading: coursesLoading,
    error: coursesError
  } = useGet(coursesUrl, {
    schema: directCoursesApiResponseSchema
  })

  const baseCourses = coursesData?.courses || []

  // Charger les traductions des cours
  useEffect(() => {
    if (baseCourses.length > 0 && language) {
      loadTranslationsForEntities('course', baseCourses, language).then(translated => {
        setTranslatedCourses(translated)
      })
    } else {
      setTranslatedCourses(baseCourses)
    }
  }, [baseCourses, language])

  const courses = translatedCourses

  // Lire les paramètres d'URL quand ils changent (pour les mises à jour après navigation)
  useEffect(() => {
    const urlSearch = searchParams.get('search')
    const urlCategory = searchParams.get('category')
    
    // Mettre à jour searchQuery
    setSearchQuery(urlSearch || "")
    
    // Mettre à jour selectedCategory
    setSelectedCategory(urlCategory || tObj.courses.all())
  }, [searchParams, tObj.courses])

  const handleSearch = (searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) {
      // Si la recherche est vide, effacer les paramètres
      setSearchQuery("")
      setSearchParams({})
      setSelectedCategory(tObj.courses.all())
      return
    }

    // Normaliser la recherche (insensible à la casse, trim)
    const normalizedSearch = searchTerm.trim().toLowerCase()

    // Vérifier si la recherche correspond à une catégorie (insensible à la casse)
    const matchingCategory = categories?.find(category => {
      const displayName = category.displayName || category.translatedName || category.name
      return displayName?.toLowerCase() === normalizedSearch
    })

    if (matchingCategory) {
      // Si c'est une catégorie, filtrer par catégorie
      setSelectedCategory(matchingCategory.name)
      setSearchQuery("")
      setSearchParams({ category: matchingCategory.name })
    } else {
      // Sinon, faire une recherche de cours (l'API va interroger la DB)
      setSearchQuery(searchTerm.trim())
      setSelectedCategory(tObj.courses.all())
      setSearchParams({ search: searchTerm.trim() })
    }
  }

  // Fonction de tri des cours (la recherche et le filtrage sont déjà faits par l'API)
  const sortedCourses = useMemo(() => {
    const coursesToSort = [...courses]
    
    switch (sortBy) {
      case "popularity":
        // Tri par popularité : note × nombre d'étudiants
        return coursesToSort.sort((a, b) => {
          const scoreA = (a.rating || 0) * (a.currentStudents || 0)
          const scoreB = (b.rating || 0) * (b.currentStudents || 0)
          return scoreB - scoreA
        })
      
      case "recent":
        // Tri par date de publication (plus récent en premier)
        return coursesToSort.sort((a, b) => {
          const dateA = new Date(a.publishedAt || a.createdAt || 0)
          const dateB = new Date(b.publishedAt || b.createdAt || 0)
          return dateB - dateA
        })
      
      case "oldest":
        // Tri par date (plus ancien en premier)
        return coursesToSort.sort((a, b) => {
          const dateA = new Date(a.publishedAt || a.createdAt || 0)
          const dateB = new Date(b.publishedAt || b.createdAt || 0)
          return dateA - dateB
        })
      
      default:
        return coursesToSort
    }
  }, [courses, sortBy])

  const categoryNames = useMemo(() => {
    const names = (categoriesLoading ? [] : categories).map(c => 
      c.displayName || c.translatedName || c.name
    ).filter(Boolean)
    return [tObj.courses.all(), ...Array.from(new Set(names))]
  }, [categories, categoriesLoading, tObj])

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    setSearchQuery("") // Effacer la recherche quand on change de catégorie
    
    // Mettre à jour les paramètres d'URL
    if (category === tObj.courses.all()) {
      setSearchParams({})
    } else {
      setSearchParams({ category })
    }
  }

  if (coursesError) {
    return (
      <div className="min-h-[60vh] bg-[#FAFBFD] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-10 h-10 text-[#DC2626] mx-auto mb-4" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Erreur</span>
          <h2 className="font-display text-3xl text-[#203a8a] mt-2">
            {tObj.courses.errorTitle()}
          </h2>
          <p className="text-[#1B2240]/70 mt-3">
            {coursesError.message || tObj.courses.errorMessage()}
          </p>
        </div>
      </div>
    )
  }

  const totalCount = courses.length

  return (
    <div className="bg-[#FAFBFD] text-[#1B2240] font-sans min-h-screen">
      {/* ============================================================
       *  Header éditorial — eyebrow + titre Fraunces + search
       * ============================================================ */}
      <section className="rule-bot bg-[#FAFBFD] relative overflow-hidden">
        {/* texture pointillée subtile */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #1B2240 1px, transparent 0)",
            backgroundSize: "4px 4px",
          }}
        />
        <div className="edito-container py-12 sm:py-16 relative">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-[#E8C547]" />
                <span className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-[#6B7280]">
                  Catalogue · Tous les modules
                </span>
              </div>
              <h1 className="font-display text-[#203a8a] tracking-tight text-balance leading-[0.98] text-[clamp(2.25rem,5.4vw,4.5rem)]">
                {tObj.courses.title()}
              </h1>
              <p className="mt-5 text-[#1B2240]/70 text-base sm:text-lg max-w-xl leading-relaxed">
                {tObj.courses.subtitle()}
              </p>
            </div>

            <div className="col-span-12 lg:col-span-5">
              <div className="border border-[#D5DAE3] bg-white focus-within:border-[#203a8a] transition-colors">
                <SearchBar
                  onSearch={handleSearch}
                  placeholder={tObj.courses.searchPlaceholder()}
                  variant="hero"
                  defaultValue={searchQuery}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
       *  Filtres catégories — chips éditoriaux
       * ============================================================ */}
      <section className="rule-bot bg-white">
        <div className="edito-container py-5 sm:py-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
              Catégories
            </span>
            <span className="h-px flex-1 bg-[#D5DAE3]" />
          </div>
          <div className="flex flex-wrap gap-2">
            {categoryNames.map((category) => {
              const active = selectedCategory === category
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryChange(category)}
                  className={`text-xs sm:text-sm px-4 py-2 border transition-all duration-300 whitespace-nowrap ${
                    active
                      ? "bg-[#203a8a] text-white border-[#203a8a]"
                      : "bg-transparent text-[#1B2240] border-[#D5DAE3] hover:border-[#203a8a] hover:text-[#203a8a]"
                  }`}
                >
                  {category}
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
       *  Listing — header (count + sort) + grille
       * ============================================================ */}
      <section className="bg-[#FAFBFD]">
        <div className="edito-container py-10 sm:py-14">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4 pb-5 border-b border-[#D5DAE3]">
            <div>
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
                {selectedCategory === tObj.courses.all()
                  ? "Tout le catalogue"
                  : selectedCategory}
              </span>
              <h2 className="font-display text-2xl sm:text-3xl text-[#203a8a] mt-1 leading-tight">
                {totalCount}{" "}
                <span className="text-[#1B2240]/60 italic font-light">
                  {selectedCategory === tObj.courses.all()
                    ? tObj.courses.coursesAvailable()
                    : `${tObj.courses.coursesIn()} ${selectedCategory}`}
                </span>
              </h2>
            </div>

            <div className="inline-flex items-center gap-3 border border-[#D5DAE3] bg-white px-3 py-2">
              <SlidersHorizontal className="w-4 h-4 text-[#6B7280]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
                Trier
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm bg-transparent text-[#1B2240] focus:outline-none cursor-pointer font-medium"
              >
                <option value="popularity">{tObj.courses.popularity()}</option>
                <option value="recent">{tObj.courses.recent()}</option>
                <option value="oldest">{tObj.courses.oldest()}</option>
              </select>
            </div>
          </div>

          {coursesLoading ? (
            <div className="py-20 text-center">
              <div className="inline-block w-10 h-10 border-2 border-[#203a8a] border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-sm uppercase tracking-[0.25em] text-[#6B7280]">
                {tObj.courses.loading()}
              </p>
            </div>
          ) : sortedCourses.length === 0 ? (
            <div className="py-20 text-center border-y border-[#D5DAE3]">
              <BookOpen className="w-10 h-10 text-[#D5DAE3] mx-auto mb-4" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
                Aucun résultat
              </span>
              <h3 className="font-display text-2xl sm:text-3xl text-[#203a8a] mt-2">
                {tObj.courses.noCoursesFound()}
              </h3>
              <p className="text-[#1B2240]/60 mt-2 max-w-md mx-auto">
                {tObj.courses.noCoursesFoundDesc()}
              </p>
            </div>
          ) : (
            <CourseGrid courses={sortedCourses} loading={false} showPrice={false} />
          )}
        </div>
      </section>
    </div>
  )
}
